"use client";

import { Camera, Video, Heart, Calendar, Clock, BookOpen, Image, Film, Award, Gift } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const deliverables = [{
  id: 1,
  icon: Camera,
  title: "Care That Starts Before the Day",
  description: "You are never just a booking in a calendar. From the first conversation, we take time to understand you, your family, and what truly matters to you. We plan around your personalities, your relationships, and the emotional rhythm of your day, so when the wedding arrives, everything feels familiar, calm, and handled.",
  ctaText: "Learn More",
  ctaLink: "#"
}, {
  id: 2,
  icon: Video,
  title: " An Experience Where You Are the Focus",
  description: "This day is about you, not the camera. Our approach is quiet, observant, and intentional. We don't compete for attention or direct every moment. Instead, we create space for you to be fully present while knowing your story is being captured with care.",
  ctaText: "View Samples",
  ctaLink: "#"
}, {
  id: 3,
  icon: Heart,
  title: "Tailored Coverage That Fits You",
  description: "No two weddings, and no two couples, are the same.\nWe don't believe in rigid packages that force your day into a template. Coverage is shaped around your timeline, your priorities, and how you want to remember the day.\n",
  ctaText: "Book Now",
  ctaLink: "#"
}, {
  id: 4,
  icon: Calendar,
  title: "A Calm Presence You Can Rely On",
  description: "Energy matters on a wedding day.\nWe bring a steady, reassuring presence that helps the day flow naturally. When emotions run high or timelines shift, we adjust quietly and confidently, without adding pressure or stress.\n",
  ctaText: "See Packages",
  ctaLink: "#"
}, {
  id: 5,
  icon: Clock,
  title: "A Unified Team, One Vision",
  description: "Nothing feels disjointed or chaotic.\nWhen photography and videography work together seamlessly, the experience feels smoother and more intentional. We move together, communicate clearly, and capture moments without crowding or repetition.\n",
  ctaText: "Learn More",
  ctaLink: "#"
}, {
  id: 6,
  icon: BookOpen,
  title: "Memories That Feel Personal, Not Generic",
  description: "Your wedding should never look like someone else's.\nWe focus on authenticity, emotion, and natural interaction, not trends that fade or poses that feel forced. Every image and every frame reflects who you are and how the day truly felt.\nWhen you look back, it still feels like you.\n",
  ctaText: "View Albums",
  ctaLink: "#"
}, {
  id: 7,
  icon: Image,
  title: "Space to Be Present",
  description: "You don't need to manage anything.\n\nWe handle the details quietly in the background, so you can stay connected to your partner, your family, and your guests. No constant interruptions, no pulling you away from moments that matter.\n\nYou remember the day for how it felt, not how busy it was.\n\n\n",
  ctaText: "Get Details",
  ctaLink: "#"
}, {
  id: 8,
  icon: Film,
  title: "Thoughtful Communication, Always",
  description: "Clear, calm communication changes everything.\nYou'll always know what's happening, what to expect, and how things will flow, without feeling overwhelmed. We guide when needed and step back when not.\nYou feel informed, comfortable, and confident from start to finish.\n",
  ctaText: "Watch Examples",
  ctaLink: "#"
}, {
  id: 9,
  icon: Award,
  title: "A Record That Grows More Valuable With Time",
  description: "These aren't just wedding photos and videos.\nThey become part of your family history. Something you return to on anniversaries, something future generations will see. Captured with care, intention, and respect for the weight those memories carry.\nThat's the difference thoughtful wedding photography and videography in Sydney makes.\n",
  ctaText: "Our Awards",
  ctaLink: "#"
}];
function DeliverableItem({
  deliverable,
  index
}: {
  deliverable: typeof deliverables[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const Icon = deliverable.icon;
  return <div ref={ref} data-index={index} className={`grid grid-cols-[auto_1fr] gap-6 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
    transitionDelay: `${index % 2 * 75}ms`
  }}>
      <div className="flex items-start justify-center pt-1">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 transition-transform duration-[var(--duration-fast)] ease-[var(--ease-spring)] hover:scale-105">
          <Icon className="size-6 text-[#B86A5A] transition-colors duration-[var(--duration-fast)]" />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-semibold text-foreground">{deliverable.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{deliverable.description}</p>
      </div>
    </div>;
}
export default function Deliverables() {
  return <section className="py-16 px-4 bg-background">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="mb-8">
            <Link to="mailto:aidan.bartlett.photography@gmail.com">
              
            </Link>
          </div>

          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
            What Will You Get?
          </h2>

          <div className="mb-4">
            <Link to="mailto:aidan.bartlett.photography@gmail.com">
              <Button size="lg" className="rounded-md bg-[#B86A5A] text-white px-8 py-4 text-base font-semibold shadow-medium transition-all duration-[120ms] ease-in-out hover:scale-[1.03] hover:bg-[#A55A4A] hover:shadow-strong focus-visible:ring-2 focus-visible:ring-[#B86A5A]/40">Book Your Wedding Story</Button>
            </Link>
          </div>

          <p className="text-muted-foreground max-w-2xl mx-auto">
            Every package includes these carefully curated deliverables designed to preserve your wedding memories beautifully.
          </p>
        </div>

        <div className="grid gap-8">
          {deliverables.map((deliverable, i) => <DeliverableItem key={deliverable.id} deliverable={deliverable} index={i} />)}
        </div>
      </div>
    </section>;
}